/**
 * Contexto global para gerenciamento de permissões de usuários
 * 
 * Centraliza o fetch de permissões em um único Provider, evitando
 * múltiplas chamadas RPC quando vários componentes usam usePermissions().
 * 
 * Antes: cada instância de usePermissions() disparava sua própria chamada RPC.
 * Agora: o PermissionsProvider faz 1 fetch e compartilha via Context.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import type { AppRole, MenuPermission, PermissionsState } from '@/hooks/usePermissions';

// Interface do valor exposto pelo contexto
interface PermissionsContextType extends PermissionsState {
  canView: (menuKey: string) => boolean;
  canCreate: (menuKey: string) => boolean;
  canEdit: (menuKey: string) => boolean;
  canDelete: (menuKey: string) => boolean;
  isSupervisor: boolean;
  isAdmin: boolean;
  refetch: () => Promise<void>;
}

// Contexto com valor inicial null (validado no hook consumidor)
const PermissionsContext = createContext<PermissionsContextType | null>(null);

/**
 * Provider que busca permissões UMA VEZ e compartilha com toda a árvore.
 * Deve envolver os componentes que utilizam usePermissions().
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user, currentTenant } = useAuth();

  const [state, setState] = useState<PermissionsState>({
    loading: true,
    userRole: null,
    permissions: [],
    permissionProfileId: null,
  });

  /**
   * Busca role e permissões do usuário via Supabase
   * Chamado apenas quando user ou tenant mudam
   */
  const fetchPermissions = useCallback(async () => {
    if (!user?.id || !currentTenant?.id) {
      setState({
        loading: false,
        userRole: null,
        permissions: [],
        permissionProfileId: null,
      });
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      // Server-side: substitui SELECT direto em user_roles por RPC
      const { data: roleRows, error: roleError } = await supabase
        .rpc('get_my_role_and_profile');

      if (roleError) throw roleError;

      const roleData = Array.isArray(roleRows) ? roleRows[0] : null;
      const userRole = (roleData?.role as AppRole) || null;
      const profileId = roleData?.permission_profile_id || null;

      // Busca permissões via função SQL
      const { data: permData, error: permError } = await supabase
        .rpc('get_user_permissions', {
          _user_id: user.id,
          _tenant_id: currentTenant.id,
        });

      if (permError) throw permError;

      setState({
        loading: false,
        userRole,
        permissions: permData || [],
        permissionProfileId: profileId,
      });
    } catch (error) {
      console.error('[PermissionsProvider] Erro ao buscar permissões:', error);
      setState({
        loading: false,
        userRole: null,
        permissions: [],
        permissionProfileId: null,
      });
    }
  }, [user?.id, currentTenant?.id]);

  // Busca permissões quando user ou tenant mudam
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // --- Funções de verificação de permissão (memoizadas) ---

  const canView = useCallback((menuKey: string): boolean => {
    if (state.loading) return false;
    if (state.userRole === 'root_admin' || state.userRole === 'tenant_admin') return true;
    const permission = state.permissions.find(p => p.menu_key === menuKey);
    return permission?.can_view ?? false;
  }, [state.loading, state.userRole, state.permissions]);

  const canCreate = useCallback((menuKey: string): boolean => {
    if (state.loading) return false;
    if (state.userRole === 'root_admin' || state.userRole === 'tenant_admin') return true;
    const permission = state.permissions.find(p => p.menu_key === menuKey);
    return permission?.can_create ?? false;
  }, [state.loading, state.userRole, state.permissions]);

  const canEdit = useCallback((menuKey: string): boolean => {
    if (state.loading) return false;
    if (state.userRole === 'root_admin' || state.userRole === 'tenant_admin') return true;
    const permission = state.permissions.find(p => p.menu_key === menuKey);
    return permission?.can_edit ?? false;
  }, [state.loading, state.userRole, state.permissions]);

  const canDelete = useCallback((menuKey: string): boolean => {
    if (state.loading) return false;
    if (state.userRole === 'root_admin' || state.userRole === 'tenant_admin') return true;
    const permission = state.permissions.find(p => p.menu_key === menuKey);
    return permission?.can_delete ?? false;
  }, [state.loading, state.userRole, state.permissions]);

  const isSupervisor = state.userRole === 'tenant_supervisor';
  const isAdmin = state.userRole === 'root_admin' || state.userRole === 'tenant_admin';

  return (
    <PermissionsContext.Provider value={{
      ...state,
      canView,
      canCreate,
      canEdit,
      canDelete,
      isSupervisor,
      isAdmin,
      refetch: fetchPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * Hook interno para consumir o contexto de permissões.
 * Lança erro se usado fora do PermissionsProvider.
 */
export function usePermissionsContext() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissionsContext deve ser usado dentro de PermissionsProvider');
  }
  return context;
}
